import { lazy, Suspense, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { NavLink, Navigate, Route, Routes } from 'react-router';

import { ApiError } from './api/client';
import { authApi } from './api/admin';
import LoginPage from './pages/LoginPage';
import MetricsPanel from './pages/MetricsPanel';
import UsersPanel from './pages/UsersPanel';
import ParameterSetsPanel from './pages/ParameterSetsPanel';
import AlertsPanel from './pages/AlertsPanel';
import BlogPostsPage from './pages/blog/BlogPostsPage';
import BlogTagsPage from './pages/blog/BlogTagsPage';
import BlogMediaPage from './pages/blog/BlogMediaPage';
import BlogTopicsPage from './pages/blog/BlogTopicsPage';
import BlogAutomationPage from './pages/blog/BlogAutomationPage';

// The editor brings ProseMirror with it — about two thirds of the bundle. Someone opening the
// console to look at metrics or acknowledge an alert should not download a text editor first.
const BlogPostEditorPage = lazy(() => import('./pages/blog/BlogPostEditorPage'));

/**
 * Sections are routes, not a piece of local state. They used to be a `useState` tab, which meant a
 * refresh always landed on Metrics and nothing could be linked to — including the alert emails,
 * whose "open in the console" link pointed at /alerts and arrived at Metrics. An editor makes it
 * worse: a post being written has to have an address, or a reload throws the work away.
 *
 * nginx already falls back to index.html for unknown paths, so deep links work in production.
 */
const SECTIONS: [string, string, boolean][] = [
  ['/', 'Metrics', true],
  ['/alerts', 'Alerts', false],
  ['/users', 'Users', false],
  ['/parameters', 'Parameter sets', false],
  ['/blog', 'Blog', false],
];

export default function App() {
  const [signedIn, setSignedIn] = useState<boolean | null>(null);

  const { data, error, refetch } = useQuery({
    queryKey: ['me'],
    queryFn: authApi.me,
    retry: false,
  });

  useEffect(() => {
    if (data) {
      // The admin app is useless to a non-admin: every endpoint it calls is behind AdminOnly.
      setSignedIn(data.isAdmin);
    } else if (error instanceof ApiError && error.status === 401) {
      setSignedIn(false);
    }
  }, [data, error]);

  if (signedIn === null) {
    return <p className="page muted">Loading…</p>;
  }

  if (!signedIn) {
    return (
      <LoginPage
        notAdmin={data?.isAdmin === false}
        onSignedIn={() => {
          void refetch();
        }}
      />
    );
  }

  return (
    <div className="page">
      <header className="topbar">
        <span className="brand">Danak Admin</span>
        <nav className="tabs">
          {SECTIONS.map(([to, label, end]) => (
            <NavLink key={to} to={to} end={end} className={({ isActive }) => (isActive ? 'tab is-active' : 'tab')}>
              {label}
            </NavLink>
          ))}
          <button
            type="button"
            className="tab"
            onClick={async () => {
              await authApi.logout();
              setSignedIn(false);
            }}
          >
            Sign out
          </button>
        </nav>
      </header>

      <Suspense fallback={<p className="muted">Loading…</p>}>
        <Routes>
          <Route path="/" element={<MetricsPanel />} />
          <Route path="/alerts" element={<AlertsPanel />} />
          <Route path="/users" element={<UsersPanel />} />
          <Route path="/parameters" element={<ParameterSetsPanel />} />
          <Route path="/blog" element={<BlogPostsPage />} />
          <Route path="/blog/new" element={<BlogPostEditorPage />} />
          <Route path="/blog/tags" element={<BlogTagsPage />} />
          <Route path="/blog/media" element={<BlogMediaPage />} />
          <Route path="/blog/topics" element={<BlogTopicsPage />} />
          <Route path="/blog/automation" element={<BlogAutomationPage />} />
          <Route path="/blog/:id" element={<BlogPostEditorPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </div>
  );
}
